import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';

interface AsyncState<T> {
  data?: T;
  error?: unknown;
  loading: boolean;
  reload: () => void;
}

/** Runs `fn` whenever `deps` change, dropping results of runs that were superseded. */
export function useAsync<T>(fn: () => Promise<T>, deps: DependencyList): AsyncState<T> {
  const [state, setState] = useState<{ data?: T; error?: unknown; loading: boolean }>({ loading: true });
  const [nonce, setNonce] = useState(0);
  const run = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memo = useCallback(fn, deps);

  useEffect(() => {
    const id = ++run.current;
    setState((s) => ({ data: s.data, loading: true }));
    memo().then(
      (data) => id === run.current && setState({ data, loading: false }),
      (error) => id === run.current && setState({ error, loading: false }),
    );
  }, [memo, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}
